import {
  type StyleProperty, // [TODO]: move this to text shadow connect
  type TextShadow,
  type Unit,
  type StyleStore$ValueType,
  type LengthValue as StyleStore$LengthValue,
  type PercentValue as StyleStore$PercentValue,
  type AngleValue as StyleStore$AngleValue,
  type OriginValue as StyleStore$OriginValue,
} from '@packages/systems/style-legacy/values';

import type {
  AngleValue,
  AngleUnit,
  AutoableLengthValue,
  AutoValue,
  LengthValue,
  KeywordValue,
  MillisecondValue,
  NoneValue,
  NormalValue,
  NoneableLengthValue,
  PercentValue,
  ColorValue,
  TextShadowValue,
  BoxShadowValue,
  NumberValue,
  PositionValue,
  CustomRawPrimitive,
} from '@packages/shared/LegacyStyleSystem/utils/StylePanelUtils';
import {
  type VariableReference,
  isVariablePlaceholder,
  isVariableReference,
  getVariableIdFromPlaceholder,
} from '@packages/systems/core/utils/StyleVariables';
import {parseValue} from '@packages/systems/style-legacy/utils';

import {
  isSwatchPlaceholder,
  getSwatchIdFromPlaceholder,
} from '@packages/systems/style-legacy/swatches';

import {emptyArray} from '@packages/utilities/fp/utils';
import {containsCssCustomValue} from '@packages/systems/style/raw-values';

// Flow typed version of the needed StyleStoreUtils.
// Copied over here to ensure these don't mess with prod while integrating.
// StyleStoreUtils renderer breakdown
// ------------------------------------------------------------------------------
// def.value:      id -> local -> upstream ->             -> browser ->         -> null
// display.value:  id -> local -> upstream ->                                   -> null
// display.value2: id -> local -> upstream -> placeholder -> browser -> default -> null
// display.value3: id -> local -> upstream -> placeholder ->         -> default -> null
// computed.value: local -> upstream ->                                   -> null
//
// def.units:      local -> upstream ->             ->         ->         -> null
// display.units:  local -> upstream -> placeholder ->         ->         -> '-'
// display.units3: local -> upstream -> placeholder ->         -> default -> null
// computed.units: local -> upstream ->             -> browser ->         -> null
// ------------------------------------------------------------------------------

export const hasLocalValue = <
  V,
  T extends {
    readonly value: V;
    readonly unit: any;
  },
>(
  property: StyleProperty<T>
): boolean => {
  return property.local.value !== null;
};

export const hasUpstreamValue = <
  T extends {
    readonly value: any;
    unit: any;
  },
>(
  property: StyleProperty<T>
): boolean => {
  return property.upstream.value !== null;
};

export const hasUserValue = <
  T extends {
    readonly value: any;
    unit: any;
  },
>(
  property: StyleProperty<T>
): boolean => {
  return userValue(property) !== null;
};

// display.value
export const userValue = <
  V,
  T extends {
    readonly value: V;
    readonly unit: any;
  },
>(
  property: StyleProperty<T>
): V | null => {
  if (property.id.value !== null) {
    return property.id.value;
  }

  if (property.local.value !== null) {
    return property.local.value;
  }
  if (property.upstream.value !== null) {
    return property.upstream.value;
  }
  return null;
};

// display.units
export const userUnit = <
  V,
  U,
  T extends {
    readonly value: V;
    readonly unit: U;
  },
>(
  obj: StyleProperty<T>
): U | null => {
  if (obj.id.value !== null) {
    return obj.id.unit || null;
  }
  if (obj.local.value !== null) {
    return obj.local.unit || null;
  }
  if (obj.upstream.value !== null) {
    return obj.upstream.unit || null;
  }
  if (obj.placeholder.value !== null) {
    return obj.placeholder.unit || null;
  }
  return null;
};

// display.units3
export const getPropertyUnitWithDefault = <T extends StyleStore$ValueType>(
  property: StyleProperty<T>
): T['value'] => {
  const unit = userUnit(property);
  if (unit) {
    return unit;
  }
  if (property.default.unit !== null) {
    return property.default.unit;
  }
  return null;
};

export const getCombinedValue = <T extends StyleStore$ValueType>(
  property: StyleProperty<T>
): string | null => {
  const value = getPropertyValueWithBrowserDefault(property);
  if (value === null) {
    return null;
  } else {
    const unit = getPropertyUnitWithDefault(property);
    return String(value) + (unit || '');
  }
};

// def.value
export const userBrowserValue = <T extends StyleStore$ValueType>(
  property: StyleProperty<T>
): T['value'] => {
  if (property.id.value !== null) {
    return property.id.value;
  }

  if (property.local.value !== null) {
    return property.local.value;
  }
  if (property.upstream.value !== null) {
    return property.upstream.value;
  }
  if (property.browser.value !== null) {
    return property.browser.value;
  }
  return null;
};

// display.value2
export const getPropertyValueWithBrowserDefault = <
  T extends StyleStore$ValueType,
>(
  value: StyleProperty<T>
): T['value'] => {
  const display = userValue(value);
  if (display !== null) {
    return display;
  }

  const placeholder = value.placeholder.value;
  if (placeholder !== null) {
    return placeholder;
  }

  const browser = value.browser.value;
  if (browser !== null) {
    return browser;
  }

  const defaultValue = value.default.value;
  if (typeof defaultValue === 'string' || typeof defaultValue === 'number') {
    return defaultValue;
  }

  return null;
};

// display.value3
export const getPropertyValueWithDefault = <T extends StyleStore$ValueType>(
  value: StyleProperty<T>
): T['value'] => {
  const display = userValue(value);
  if (display !== null) {
    return display;
  }

  const placeholder = value.placeholder.value;
  if (placeholder !== null) {
    return placeholder;
  }

  const defaultValue = value.default.value;
  if (typeof defaultValue === 'string' || typeof defaultValue === 'number') {
    return defaultValue;
  }

  return null;
};

export const getVariablePropertyValueWithDefault = <
  T extends StyleStore$ValueType,
>(
  value: StyleProperty<T>
): T['value'] | VariableReference => {
  const value_ = getPropertyValueWithDefault(value);
  const variableRef = getVariablePlaceholderValue(value_);
  if (variableRef) {
    return variableRef;
  }
  return value_;
};

// computed.value
export const computedValue = <T extends StyleStore$ValueType>(
  value: StyleProperty<T> | string
): T['value'] => {
  if (typeof value === 'undefined' || value === null) {
    return null;
  }

  if (typeof value === 'string') {
    value = {
      local: {
        // @ts-expect-error - TS2322 - Type '{ value: string; }' is not assignable to type 'StyleStore$EmptyValue | T'.
        value,
      },
    };
  }

  // @ts-expect-error - TS2339 - Property 'local' does not exist on type 'string | StyleProperty<T>'. | TS2339 - Property 'local' does not exist on type 'string | StyleProperty<T>'.
  const localValue = value.local && value.local.value;
  if (typeof localValue !== 'undefined' && localValue !== null) {
    return localValue;
  }

  // @ts-expect-error - TS2339 - Property 'upstream' does not exist on type 'string | StyleProperty<T>'. | TS2339 - Property 'upstream' does not exist on type 'string | StyleProperty<T>'.
  const upstreamValue = value.upstream && value.upstream.value;
  if (typeof upstreamValue !== 'undefined' && upstreamValue !== null) {
    return upstreamValue;
  }

  return null;
};

// computed.units
export const computedUnits = <T extends StyleStore$ValueType>(
  value: StyleProperty<T>
): T['unit'] => {
  if (value.local.value !== null) {
    return value.local.unit;
  }

  if (value.upstream.value !== null) {
    return value.upstream.unit;
  }

  if (value.browser.value !== null) {
    return value.browser.unit;
  }

  return null;
};

// Renderers - takes a style store property and converts to a style panel value

export const AUTO_UNIT_VALUE: AutoValue = {value: 'auto', unit: ''};
export const NONE_UNIT_VALUE: NoneValue = {value: 'none', unit: ''};
export const NORMAL_UNIT_VALUE: NormalValue = {value: 'normal', unit: ''};
export const ZERO_PX_VALUE = {value: 0, unit: 'px'} as const;

export const isAutoUnitValue = (val: {
  value: any;
  unit: any;
}): val is AutoValue =>
  val.value === 'auto' && (val.unit === '' || val.unit === null);

export const isNoneUnitValue = (val: {
  value: any;
  unit: any;
}): val is NoneValue =>
  val.value === 'none' && (val.unit === '' || val.unit === null);

// [TODO]: refine StyleStore$ValueType -> StyleStore$UnitType
export const getUnitValue = <T extends StyleStore$ValueType>(
  property: StyleProperty<T>
): AutoableLengthValue => {
  const value = userValue(property);
  const unit = userUnit(property);
  if (value === 'auto') {
    return AUTO_UNIT_VALUE;
  } else if (value === '' || value === 'none' || value === null) {
    return ZERO_PX_VALUE;
  } else if (unit === null) {
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type 'number | "auto"'.
    return {value, unit: 'px'};
  } else {
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type '"" | LengthUnit'.
    return {value: value as number, unit};
  }
};

export const getMillisecondValue = <
  T extends {
    value: number;
    unit: 'ms';
  },
>(
  property: StyleProperty<T>
): MillisecondValue => {
  const value = userValue(property);
  const unit = userUnit(property);
  if (value === null) {
    return {value: 0, unit: 'ms'};
  } else if (unit === null) {
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type 'number'.
    return {value, unit: 'ms'};
  } else {
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type 'number'. | TS2322 - Type '{} | undefined' is not assignable to type '"ms"'.
    return {value, unit};
  }
};

export const getLengthValue = <
  V extends number | 'none' | null,
  U extends StyleStore$LengthValue['unit'] | null,
  T extends {
    readonly value: V;
    readonly unit: U;
  },
>(
  property: StyleProperty<T>
): LengthValue => {
  const value = userValue(property);
  const unit = userUnit(property);
  if (value === null || value === 'none') {
    return ZERO_PX_VALUE;
  } else if (unit === null) {
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type 'number'.
    return {value, unit: 'px'};
  }
  // value <: number | null
  // unit <: 'px' | 'em' | 'rem' | 'vh' | 'vw' | '%' | null
  // Ignoring the nulls, returning {value, unit} would be:
  // { value: number, unit: 'px' | 'em' | 'rem' | ..etc.. };
  // We need the return type to be:
  // { value: number, unit: 'px'} | {value: number, unit: 'em' } | ..etc..
  //
  // Since the expected return type is a union of distinct unit values we have
  // Abstractly: T<A> | T<B> !== T<A | B>
  // Or: UnitValue<A> <: UnitValue<A | B> but UnitValue<A | B> </: UnitValue<A>
  //
  // There has got to be a better way to handle this than all these ifs.
  else if (unit === 'em') {
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type 'number'.
    return {value, unit: 'em'};
  } else if (unit === 'rem') {
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type 'number'.
    return {value, unit: 'rem'};
  } else if (unit === 'vh') {
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type 'number'.
    return {value, unit: 'vh'};
  } else if (unit === 'vw') {
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type 'number'.
    return {value, unit: 'vw'};
  } else if (unit === 'dvh') {
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type 'number'.
    return {value, unit: 'dvh'};
  } else if (unit === 'dvw') {
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type 'number'.
    return {value, unit: 'dvw'};
  } else if (unit === 'svh') {
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type 'number'.
    return {value, unit: 'svh'};
  } else if (unit === 'svw') {
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type 'number'.
    return {value, unit: 'svw'};
  } else if (unit === 'lvh') {
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type 'number'.
    return {value, unit: 'lvh'};
  } else if (unit === 'lvw') {
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type 'number'.
    return {value, unit: 'lvw'};
  } else if (unit === 'vmin') {
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type 'number'.
    return {value, unit: 'vmin'};
  } else if (unit === 'vmax') {
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type 'number'.
    return {value, unit: 'vmax'};
  } else if (unit === 'ch') {
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type 'number'.
    return {value, unit: 'ch'};
  } else {
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type 'number'. | TS2322 - Type '{} | undefined' is not assignable to type 'LengthUnit'.
    return {value, unit};
  }
};

export const getVariableLengthValue = <T extends StyleStore$ValueType>(
  property: StyleProperty<T>
): LengthValue | VariableReference | CustomRawPrimitive => {
  const value = getPropertyValueWithBrowserDefault(property);
  const variableRef = getVariablePlaceholderValue(value);
  if (variableRef) {
    return variableRef;
  }

  const customRawValue = getCustomRawValue(value);
  if (customRawValue) {
    return customRawValue;
  }

  return getLengthValue(property);
};

export const getPositionValue = <T extends StyleStore$OriginValue>(
  property: StyleProperty<T>
): PositionValue => {
  const value = getKeywordValue(property);
  const [x = '50%', y = '50%'] = value.split(' ');
  const xUnitValue = parseValue(x);
  const yUnitValue = parseValue(y);
  return {
    x: {value: xUnitValue.value, unit: xUnitValue.unit} as LengthValue,
    y: {value: yUnitValue.value, unit: yUnitValue.unit} as LengthValue,
  };
};

export const getLengthOrPercentValue = <
  V extends number | 'none' | null,
  U extends StyleStore$LengthValue | StyleStore$PercentValue['unit'] | null,
  T extends {
    readonly value: V;
    readonly unit: U;
  },
>(
  property: StyleProperty<T>
): LengthValue | PercentValue => {
  const value = userValue(property);
  const unit = userUnit(property);
  if (value === null || value === 'none') {
    return ZERO_PX_VALUE;
  } else if (unit === null) {
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type 'number'.
    return {value, unit: 'px'};
  }
  // value <: number | null
  // unit <: 'px' | 'em' | 'rem' | 'vh' | 'vw' | 'dvh' | 'dvw' | 'svh' | 'svw' | 'lvh' | 'lvw' | '%' | null
  // Ignoring the nulls, returning {value, unit} would be:
  // { value: number, unit: 'px' | 'em' | 'rem' | ..etc.. };
  // We need the return type to be:
  // { value: number, unit: 'px'} | {value: number, unit: 'em' } | ..etc..
  //
  // Since the expected return type is a union of distinct unit values we have
  // Abstractly: T<A> | T<B> !== T<A | B>
  // Or: UnitValue<A> <: UnitValue<A | B> but UnitValue<A | B> </: UnitValue<A>
  //
  // There has got to be a better way to handle this than all these ifs.
  else if (unit === 'em') {
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type 'number'.
    return {value, unit: 'em'};
  } else if (unit === '%') {
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type 'number'.
    return {value, unit: '%'};
  } else if (unit === 'rem') {
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type 'number'.
    return {value, unit: 'rem'};
  } else if (unit === 'vh') {
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type 'number'.
    return {value, unit: 'vh'};
  } else if (unit === 'vw') {
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type 'number'.
    return {value, unit: 'vw'};
  } else if (unit === 'dvh') {
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type 'number'.
    return {value, unit: 'dvh'};
  } else if (unit === 'dvw') {
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type 'number'.
    return {value, unit: 'dvw'};
  } else if (unit === 'svh') {
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type 'number'.
    return {value, unit: 'svh'};
  } else if (unit === 'svw') {
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type 'number'.
    return {value, unit: 'svw'};
  } else if (unit === 'lvh') {
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type 'number'.
    return {value, unit: 'lvh'};
  } else if (unit === 'lvw') {
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type 'number'.
    return {value, unit: 'lvw'};
  } else if (unit === 'vmin') {
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type 'number'.
    return {value, unit: 'vmin'};
  } else if (unit === 'vmax') {
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type 'number'.
    return {value, unit: 'vmax'};
  } else if (unit === 'ch') {
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type 'number'.
    return {value, unit: 'ch'};
  } else {
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type 'number'.
    return {value, unit: 'px'};
  }
};

export const getVariableLengthOrPercentValue = <T extends StyleStore$ValueType>(
  property: StyleProperty<T>
): LengthValue | PercentValue | VariableReference | CustomRawPrimitive => {
  const value = getPropertyValueWithBrowserDefault(property);
  const variableRef = getVariablePlaceholderValue(value);
  if (variableRef) {
    return variableRef;
  }

  const customRawValue = getCustomRawValue(value);
  if (customRawValue) {
    return customRawValue;
  }

  return getLengthOrPercentValue(property);
};

export const getVariableNumberValue = <T extends StyleStore$ValueType>(
  property: StyleProperty<T>
): NumberValue | VariableReference | CustomRawPrimitive => {
  const value = getPropertyValueWithBrowserDefault(property);
  const variableRef = getVariablePlaceholderValue(value);
  if (variableRef) {
    return variableRef;
  }

  const customRawValue = getCustomRawValue(value);
  if (customRawValue) {
    return customRawValue;
  }

  return getNumberValue(property);
};

export const getVariablePercentValue = <T extends StyleStore$ValueType>(
  property: StyleProperty<T>
): PercentValue | VariableReference | CustomRawPrimitive => {
  const value = getPropertyValueWithBrowserDefault(property);
  const variableRef = getVariablePlaceholderValue(value);
  if (variableRef) {
    return variableRef;
  }

  const customRawValue = getCustomRawValue(value);
  if (customRawValue) {
    return customRawValue;
  }

  return getPercentValue(property);
};

export const getVariableNumberAutoValue = <T extends StyleStore$ValueType>(
  property: StyleProperty<T>
): NumberValue | AutoValue | VariableReference | CustomRawPrimitive => {
  const value = getPropertyValueWithBrowserDefault(property);
  const variableRef = getVariablePlaceholderValue(value);
  if (variableRef) {
    return variableRef;
  }

  const customRawValue = getCustomRawValue(value);
  if (customRawValue) {
    return customRawValue;
  }

  if (value === 'auto') {
    return AUTO_UNIT_VALUE;
  }

  return getNumberValue(property);
};

export const getAngleValue = <
  V extends number | null,
  U extends StyleStore$AngleValue['unit'] | null,
  T extends {
    readonly value: V;
    readonly unit: U;
  },
>(
  property: StyleProperty<T>
): AngleValue => {
  const rawValue = userValue(property);
  const value: number = typeof rawValue === 'number' ? rawValue : 0;

  const rawUnit = userUnit(property);

  let unit: AngleUnit;

  switch (rawUnit) {
    case 'rad':
    case 'grad':
    case 'turn':
      unit = rawUnit;
      break;
    default:
      unit = 'deg';
  }

  return {value, unit};
};

export const getPercentValue = <
  V extends number | null,
  U extends '%' | null,
  T extends {
    readonly value: V;
    readonly unit: U;
  },
>(
  property: StyleProperty<T>
): PercentValue => {
  const value = userValue(property);
  const unit = userUnit(property);
  if (value === null) {
    return {value: 0, unit: '%'};
  } else if (unit === null) {
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type 'number'.
    return {value, unit: '%'};
  } else {
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type 'number'. | TS2322 - Type '{} | undefined' is not assignable to type '"%"'.
    return {value, unit};
  }
};

export const getDropShadowValue = <
  V extends string | null,
  U extends null,
  T extends {readonly value: V; readonly unit: U},
>(
  property: StyleProperty<T>
): KeywordValue<string> => {
  const value = userValue(property);
  if (value === null) {
    return {value: '', unit: ''};
  } else {
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type 'string'.
    return {value, unit: ''};
  }
};

// [TODO]: refine StyleStore$ValueType -> StyleStore$UnitlessType
// NumberValue: flex-grow, flex-shrink, line-height, z-index
export const getNumberValue = <T extends StyleStore$ValueType>(
  property: StyleProperty<T>
): NumberValue => {
  const value = userValue(property);
  return {
    // @ts-expect-error - handle empty values
    value: typeof value === 'number' ? value : null,
    unit: '-',
  };
};

// [TODO]: refine StyleStore$ValueType -> StyleStore$UnitlessType
// AutoValue | NumberValue: z-index, columnCount
export const getAutoNumberValue = <T extends StyleStore$ValueType>(
  property: StyleProperty<T>
): AutoValue | NumberValue | CustomRawPrimitive => {
  const value = userValue(property);
  if (typeof value === 'number') {
    return {value, unit: '-'};
  }

  const customRawValue = getCustomRawValue(value);
  if (customRawValue) {
    return customRawValue;
  }

  return AUTO_UNIT_VALUE;
};

// [TODO]: refine StyleStore$ValueType -> StyleStore$UnitType
export const getAutoLengthValue = <T extends StyleStore$ValueType>(
  property: StyleProperty<T>
): AutoableLengthValue => {
  const value = getPropertyValueWithBrowserDefault(property);
  const unit = userUnit(property);
  if (
    value === null ||
    value === 'auto' ||
    // account for CssPropertyUtils#placeholder being Auto
    value === 'Auto'
  ) {
    return AUTO_UNIT_VALUE;
  } else if (unit === null) {
    return {value, unit: 'px'};
  } else {
    // account for CssPropertyUtils#placeholder being a string "0"
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type '"" | LengthUnit'.
    return {value: Number(value as any) as number, unit};
  }
};

// [TODO]: refine StyleStore$ValueType -> StyleStore$UnitType
export const getAutoLengthValueOrPercentValue = <
  T extends StyleStore$ValueType,
>(
  property: StyleProperty<T>
): AutoableLengthValue | PercentValue => {
  const value: null | number | 'auto' | 'Auto' | '0' =
    getPropertyValueWithBrowserDefault(property);
  const unit = userUnit(property);
  // value could be one of the following:
  // - null
  // - 'auto'
  // - 'Auto'
  // - <number>
  // - '<number>'
  if (value === null || value === 'auto' || value === 'Auto') {
    return AUTO_UNIT_VALUE;
  } else if (typeof value === 'number' && unit !== null) {
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type '"" | "%" | LengthUnit'.
    return {value, unit};
  } else if (typeof value === 'number' && unit === null) {
    return {value, unit: 'px'};
    // @ts-expect-error - TS2345 - Argument of type 'number | "0"' is not assignable to parameter of type 'number'.
  } else if (isNaN(value) === false && unit === null) {
    return {value: Number(value), unit: 'px'};
    // @ts-expect-error - TS2345 - Argument of type 'number | "0"' is not assignable to parameter of type 'number'.
  } else if (isNaN(value) === false && unit !== null) {
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type '"" | "%" | LengthUnit'.
    return {value: Number(value), unit};
  } else if (unit == null) {
    return {value: Number(value), unit: 'px'};
  } else {
    // @ts-expect-error - TS2322 - Type '{}' is not assignable to type '"" | "%" | LengthUnit'.
    return {value: Number(value), unit};
  }
};

export const getVariableAutoLengthValueOrPercentValue = <
  T extends StyleStore$ValueType,
>(
  property: StyleProperty<T>
):
  | AutoableLengthValue
  | PercentValue
  | VariableReference
  | CustomRawPrimitive => {
  const value = getPropertyValueWithBrowserDefault(property);
  const variableRef = getVariablePlaceholderValue(value);
  if (variableRef) {
    return variableRef;
  }

  const customRawValue = getCustomRawValue(value);
  if (customRawValue) {
    return customRawValue;
  }

  return getAutoLengthValueOrPercentValue(property);
};

export const getNoneLengthValue = <T extends StyleStore$ValueType>(
  property: StyleProperty<T>
): NoneableLengthValue => {
  const value = getPropertyValueWithBrowserDefault(property);
  const unit = userUnit(property);
  if (value === null || value === 'none') {
    return NONE_UNIT_VALUE;
  } else if (unit === null) {
    return {value, unit: 'px'};
  } else {
    // account for CssPropertyUtils#placeholder being a string "0"
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type '"" | LengthUnit'.
    return {value: Number(value as any) as number, unit};
  }
};

const getNoneLengthValueOrPercentValue = <T extends StyleStore$ValueType>(
  property: StyleProperty<T>
): NoneableLengthValue | PercentValue => {
  const value: null | number | 'none' | 'None' | '0' =
    getPropertyValueWithBrowserDefault(property);
  const unit = userUnit(property);

  if (value === null || value === 'none' || value === 'None') {
    return NONE_UNIT_VALUE;
  } else if (typeof value === 'number' && unit !== null) {
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type '"" | "%" | LengthUnit'.
    return {value, unit};
  } else if (typeof value === 'number' && unit === null) {
    return {value, unit: 'px'};
    // @ts-expect-error - TS2345 - Argument of type 'number | "0"' is not assignable to parameter of type 'number'.
  } else if (isNaN(value) === false && unit === null) {
    return {value: Number(value), unit: 'px'};
    // @ts-expect-error - TS2345 - Argument of type 'number | "0"' is not assignable to parameter of type 'number'.
  } else if (isNaN(value) === false && unit !== null) {
    // @ts-expect-error - TS2322 - Type '{} | undefined' is not assignable to type '"" | "%" | LengthUnit'.
    return {value: Number(value), unit};
  } else if (unit == null) {
    return {value: Number(value), unit: 'px'};
  } else {
    // @ts-expect-error - TS2322 - Type '{}' is not assignable to type '"" | "%" | LengthUnit'.
    return {value: Number(value), unit: '-'};
  }
};

export const getVariableNoneLengthValueOrPercentValue = <
  T extends StyleStore$ValueType,
>(
  property: StyleProperty<T>
): NoneableLengthValue | PercentValue | VariableReference => {
  const value = getPropertyValueWithBrowserDefault(property);
  const variableRef = getVariablePlaceholderValue(value);
  if (variableRef) {
    return variableRef;
  }

  return getNoneLengthValueOrPercentValue(property);
};

export const getComputedValue = <T extends StyleStore$ValueType>(
  property: StyleProperty<T>
) => {
  return {
    value: computedValue(property),
    unit: computedUnits(property),
  };
};

// [TODO]: refine StyleStore$ValueType -> StyleStore$KeywordType
export const getKeywordValue = <T extends StyleStore$ValueType>(
  property: StyleProperty<T>
): T['value'] => {
  return getPropertyValueWithDefault(property);
};
export const getVariableKeywordValue = <T extends StyleStore$ValueType>(
  property: StyleProperty<T>
): T['value'] | VariableReference | CustomRawPrimitive => {
  const value = getPropertyValueWithDefault(property);
  const variableRef = getVariablePlaceholderValue(value);
  if (variableRef) {
    return variableRef;
  }

  const customRawValue = getCustomRawValue(value);
  if (customRawValue) {
    return customRawValue;
  }

  return getKeywordValue(property);
};

// [TODO]: refine StyleStore$ValueType -> StyleStore$KeywordType
export const getBrowserKeywordValue = <T extends StyleStore$ValueType>(
  property: StyleProperty<T>
): T['value'] => {
  return getPropertyValueWithBrowserDefault(property);
};

export const getVariablePlaceholderValue = (
  value: string
): null | VariableReference => {
  if (isVariablePlaceholder(value)) {
    const variableId = getVariableIdFromPlaceholder(value);
    if (variableId == null) {
      throw new Error(`Malformed variable ID placeholder format: ${value}`);
    }
    return {
      type: 'ref',
      value: {
        variableId,
      },
    };
  }
  return null;
};

const getCustomRawValue = <T extends StyleStore$ValueType>(
  value: T['value']
): null | CustomRawPrimitive => {
  if (typeof value === 'string' && containsCssCustomValue(value)) {
    return {type: 'raw', value};
  }

  return null;
};

// [TODO]: refine StyleStore$ValueType -> StyleStore$ColorType
export const getColorValue = <T extends StyleStore$ValueType>(
  property: StyleProperty<T>
): ColorValue => {
  const value = getPropertyValueWithBrowserDefault(property) || 'black';
  const variableRef = getVariablePlaceholderValue(value);
  if (variableRef) {
    return variableRef;
  }

  return isSwatchPlaceholder(value)
    ? {
        type: 'global-swatch-reference',
        id: getSwatchIdFromPlaceholder(value) || '',
      }
    : {type: 'color', value};
};

export const getVariableAutoLengthValue = <T extends StyleStore$ValueType>(
  property: StyleProperty<T>
): AutoValue | LengthValue | VariableReference => {
  const value = getPropertyValueWithBrowserDefault(property);
  const variableRef = getVariablePlaceholderValue(value);
  if (variableRef) {
    return variableRef;
  }

  return getAutoLengthValue(property);
};

// [TODO]: refine StyleStore$ValueType -> StyleStore$TextShadowType
export const getTextShadowValue = <T extends StyleStore$ValueType>(
  property: StyleProperty<T>
): Array<TextShadowValue> => {
  const value = computedValue(property);
  if (Array.isArray(value)) {
    return value.map((shadow: TextShadow) => {
      return {
        id: shadow._guid,
        type: 'text-shadow',
        hidden: computedValue(shadow.hidden),
        x: getVariableLengthValue(shadow.x),
        y: getVariableLengthValue(shadow.y),
        blur: getVariableLengthValue(shadow.blur),
        color: getColorValue(shadow.color),
      };
    });
  } else {
    return emptyArray;
  }
};

// [TODO]: refine StyleStore$ValueType -> StyleStore$BoxShadowType
export const getBoxShadowValue = <T extends StyleStore$ValueType>(
  property: StyleProperty<T>
): Array<BoxShadowValue> => {
  const value = computedValue(property);
  if (Array.isArray(value)) {
    // @ts-expect-error - TS7006 - Parameter 'shadow' implicitly has an 'any' type.
    return value.map((shadow) => ({
      id: shadow._guid,
      type: 'box-shadow',
      hidden: computedValue(shadow.hidden),
      x: getVariableLengthValue(shadow.x),
      y: getVariableLengthValue(shadow.y),
      blur: getVariableLengthValue(shadow.blur),
      color: getColorValue(shadow.color),
      size: getVariableLengthValue(shadow.spread),
      placement: getKeywordValue(shadow.position),
    }));
  } else {
    return emptyArray;
  }
};

interface StyleStore$UnitValue {
  readonly value: number;
  readonly unit: Unit;
  readonly type?: void;
}

export const unitValueChanged = (
  a: StyleStore$UnitValue | VariableReference | CustomRawPrimitive | string,
  b: StyleStore$UnitValue | VariableReference | CustomRawPrimitive | string
): boolean => {
  // string check
  if (typeof a === 'string' && typeof b === 'string') {
    return a !== b;
  } else if (typeof a === 'object' && typeof b === 'object') {
    if (a.type !== b.type) {
      return true;
    }

    if (isVariableReference(a) && isVariableReference(b)) {
      return a.value.variableId !== b.value.variableId;
    }

    if (a.type === 'raw' && b.type === 'raw') {
      return a.value !== b.value;
    }

    if (a.value !== b.value) {
      return true;
    }

    return (
      a.type !== 'raw' &&
      b.type !== 'raw' &&
      a.type !== 'ref' &&
      b.type !== 'ref' &&
      a.unit !== b.unit
    );
  } else {
    return false;
  }
};
export const colorValueChanged = <T extends ColorValue>(
  a: T,
  b: T
): boolean => {
  // changed from swatch to color or vice versa
  if (a.type !== b.type) {
    return true;
  }
  // color value changed
  if (a.type === 'color' && b.type === 'color' && a.value !== b.value) {
    return true;
  }
  // swatch id changed
  if (
    a.type === 'global-swatch-reference' &&
    b.type === 'global-swatch-reference' &&
    a.id !== b.id
  ) {
    return true;
  }
  // variable reference changed
  if (
    a.type === 'ref' &&
    b.type === 'ref' &&
    a.value.variableId !== b.value.variableId
  ) {
    return true;
  }
  return false;
};
